import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ProjectsActions from './projects.actions';
import { ProjectInterfaceWithId } from '@sk/sk-interfaces';

export const PROJECTS_FEATURE_KEY = 'projects';

export interface ProjectsState extends EntityState<ProjectInterfaceWithId> {
  projectsLoaded: boolean; // has the Clients list been loaded
  projectsLoadError?: string | null; // last none error (if any)

  queryProjectLoadError?: string | null; // last none error (if any)

  addProjectError?: string | null; // last none error when adding a new client (if any)
  addProjectSuccess: boolean; // last none error when adding a new client (if any)

  updateProjectError?: string | null; // last none error when adding a new client (if any)
  updateProjectSuccess: boolean; // last none error when adding a new client (if any)

  deleteProjectError?: string | null; // last none error when adding a new client (if any)
  deleteProjectSuccess: boolean; // last none error when adding a new client (if any)
}

export interface ProjectPartialState {
  readonly [PROJECTS_FEATURE_KEY]: ProjectsState;
}

export const projectsAdapter: EntityAdapter<ProjectInterfaceWithId> = createEntityAdapter<ProjectInterfaceWithId>({
  selectId: selectProjectId
});

export const initialState: ProjectsState = projectsAdapter.getInitialState({
  // set initial required properties
  projectsLoaded: false,
  addProjectSuccess: false,
  updateProjectSuccess: false,
  deleteProjectSuccess: false
});


export function selectProjectId(a: ProjectInterfaceWithId): string {
  //In this case this would be optional since primary key is id
  return a.id;
}


const projectsReducer = createReducer(
  initialState,

  on(ProjectsActions.loadProjects, (state) => {
    if (state.projectsLoadError) {
      delete state.projectsLoadError;
    }
    if (state.addProjectError) {
      delete state.addProjectError;
    }
    if (state.updateProjectError) {
      delete state.updateProjectError;
    }
    if (state.deleteProjectError) {
      delete state.deleteProjectError;
    }

    return ({
      ...state,
      projectsLoaded: false
    });
  }),

  on(ProjectsActions.loadProjectsSuccess, (state, { projects }) => {
    return {
      ...state,
      projectsLoaded: true
    };
  }),

  on(ProjectsActions.loadProjectsFailure, (state, { error }) => {
    if (error) {
      console.error(error);
    }
    return ({
      ...state,
      projectsLoadError: error
    });
  }),

  // ACTIONS INITIATED FROM THIS CLIENT
  // FIREBASE WILL AUTOMATICALLY UPDATE STATE THROUGH QUERY!!


  /**
   * Add Staff from Client
   */
  on(ProjectsActions.addProject, (state, { project }) => {
    if (state.addProjectError) {
      delete state.addProjectError;
    }
    return ({
      ...state,
      addProjectSuccess: false
    });
  }),

  on(ProjectsActions.addProjectError, (state, { error }) => {
    return ({
      ...state,
      addProjectError: error.message
    });
  }),

  on(ProjectsActions.addProjectSuccess, (state, {}) => {
    if (state.addProjectError) {
      delete state.addProjectError;
    }
    return ({
      ...state,
      addProjectSuccess: true
    });
  }),

  /**
   * Delete Staff from Client
   */
  on(ProjectsActions.removeProject, (state, {
    id
  }) => {
    if (state.deleteProjectError) {
      delete state.deleteProjectError;
    }
    return projectsAdapter.removeOne(id, {
      ...state,
      deleteProjectSuccess: false
    });
  }),


  on(ProjectsActions.removeProjectSuccess, (state, {}) => {
    if (state.deleteProjectError) {
      delete state.deleteProjectError;
    }
    return ({
      ...state,
      deleteProjectSuccess: true
    });
  }),


  on(ProjectsActions.removeProjectError, (state, {
    error
  }) => {
    return ({
      ...state,
      deleteProjectError: error.message
    });

  }),


  /**
   * Update staff from Client
   */
  on(ProjectsActions.updateProject, (state, { id, changes }) => {
    if (state.updateProjectError) {
      delete state.updateProjectError;
    }
    return projectsAdapter.updateOne({
        id,
        changes
      },
      {
        ...state,
        updateProjectSuccess: false
      });
  }),

  on(ProjectsActions.updateProjectSuccess, (state, {}) => {
    if (state.updateProjectError) {
      delete state.updateProjectError;
    }
    return {
      ...state,
      updateProjectSuccess: true
    };
  }),

  on(ProjectsActions.updateProjectsError, (state, { error }) => {
    return ({
      ...state,
      updateProjectError: error.message
    });
  }),


  /**
   * FIRSTORE ACTIONS
   */
  on(ProjectsActions.fromFireStoreAddProject, (state, { project }) => {
    if (state.queryProjectLoadError) {
      delete state.queryProjectLoadError;
    }
    return projectsAdapter.setOne(project, state);
  }),

  on(ProjectsActions.fromFireStoreModifyProject, (state, { project }) => {
    if (state.queryProjectLoadError) {
      delete state.queryProjectLoadError;
    }
    return projectsAdapter.updateOne({
        id: project.id,
        changes: project
      },
      state);
  }),
  on(ProjectsActions.fromFireStoreRemoveProject, (state, {
    id
  }) => {
    if (state.queryProjectLoadError) {
      delete state.queryProjectLoadError;
    }
    return projectsAdapter.removeOne(id, state);
  }),
  on(ProjectsActions.queryFireStoreProjectsError, (state, {
    error
  }) => {
    return {
      queryProjectLoadError: error.message,
      ...state
    };

  })
);


export function reducer(state: ProjectsState | undefined, action: Action) {
  return projectsReducer(state, action);
}
